import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft, FiPlus, FiCheck, FiTrash2, FiShare2, FiX,
  FiSettings, FiCopy, FiEye, FiEyeOff,
} from 'react-icons/fi';
import { getTypeMeta, STATUS_OPTIONS, getStatusMeta } from '../lib/sharedListTypes';
import ShareSettingsModal from '../components/ShareSettingsModal';
import './SharedListDetail.css';

const SharedListDetail = ({ token }) => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ subtotal: 0, paid: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ text: '', description: '', quantity: '', unitPrice: '', location: '', responsibleCompany: '' });
  const [adding, setAdding] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const meta = useMemo(() => (list ? getTypeMeta(list.type) : null), [list]);
  const cols = meta?.columns || {};

  const fetchList = async () => {
    try {
      const r = await fetch(`${apiUrl}/api/shared-lists/${listId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 404) {
        setError('This list no longer exists.');
        return;
      }
      if (!r.ok) throw new Error('Failed to load list');
      const d = await r.json();
      setList(d.sharedList);
      setItems(d.items || []);
      setTotals(d.totals || { subtotal: 0, paid: 0, remaining: 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, [listId]);

  const addItem = async () => {
    if (!draft.text.trim()) return;
    setAdding(true);
    try {
      const body = {
        listId,
        text: draft.text.trim(),
        description: draft.description.trim() || null,
        quantity: draft.quantity !== '' ? parseFloat(draft.quantity) : null,
        unitPrice: draft.unitPrice !== '' ? parseFloat(draft.unitPrice) : null,
        location: draft.location.trim() || null,
        responsibleCompany: draft.responsibleCompany.trim() || null,
        status: 'open',
      };
      const r = await fetch(`${apiUrl}/api/shared-list-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Add failed');
      }
      setDraft({ text: '', description: '', quantity: '', unitPrice: '', location: '', responsibleCompany: '' });
      fetchList();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const updateItem = async (itemId, patch) => {
    try {
      const r = await fetch(`${apiUrl}/api/shared-list-items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error('Update failed');
      fetchList();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const r = await fetch(`${apiUrl}/api/shared-list-items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Delete failed');
      fetchList();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteList = async () => {
    if (!window.confirm('Delete this entire list? This cannot be undone.')) return;
    try {
      const r = await fetch(`${apiUrl}/api/shared-lists/${listId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Delete failed');
      navigate('/shared-lists');
    } catch (e) {
      setError(e.message);
    }
  };

  const publicUrl = list?.externalToken
    ? `${window.location.origin}/share/${list.externalToken}`
    : '';

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = publicUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  if (loading) return <div className="loading-spinner"></div>;
  if (error && !list) {
    return (
      <div className="shared-list-detail-page">
        <div className="empty-state">{error}</div>
        <Link to="/shared-lists" className="btn btn-secondary">
          <FiArrowLeft size={16} /> Back to lists
        </Link>
      </div>
    );
  }

  const Icon = meta.icon;
  const showTotalsBar = cols.money && (totals.subtotal > 0 || totals.paid > 0);

  return (
    <div className="shared-list-detail-page" data-testid="shared-list-detail">
      <div className="sld-header">
        <Link to="/shared-lists" className="sld-back" data-testid="sld-back-link">
          <FiArrowLeft size={18} /> All lists
        </Link>
        <div className="sld-title-row">
          <div className="sld-title-icon" style={{ background: `${meta.color}1A`, color: meta.color }}>
            <Icon size={20} />
          </div>
          <div className="sld-title-text">
            <div className="sld-title" data-testid="sld-title">{list.title}</div>
            <div className="sld-type" style={{ color: meta.color }}>{meta.label}</div>
          </div>
          <div className="sld-actions">
            <button
              className={`btn btn-sm ${list.externalShareEnabled ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowShareModal(true)}
              data-testid="sld-share-button"
            >
              <FiShare2 size={14} />
              <span>{list.externalShareEnabled ? 'Shared' : 'Share'}</span>
            </button>
            <button className="btn btn-sm btn-danger" onClick={deleteList} data-testid="sld-delete-button">
              <FiTrash2 size={14} /> Delete List
            </button>
          </div>
        </div>
        {list.description && <div className="sld-description">{list.description}</div>}
        {list.externalShareEnabled && publicUrl && (
          <div className="sld-share-banner" data-testid="sld-share-banner">
            <FiShare2 size={14} />
            <span>Public link:</span>
            <code>{publicUrl}</code>
            <button onClick={copyLink} className="sld-share-copy" data-testid="sld-share-copy">
              <FiCopy size={12} />
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        )}
      </div>

      {showTotalsBar && (
        <div className="sld-totals" data-testid="sld-totals">
          <div className="sld-totals-card">
            <div className="sld-totals-label">Subtotal</div>
            <div className="sld-totals-value" data-testid="sld-subtotal">
              {Number(totals.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="sld-totals-card">
            <div className="sld-totals-label">Paid</div>
            <div className="sld-totals-value" data-testid="sld-paid" style={{ color: '#1DBF73' }}>
              {Number(totals.paid).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="sld-totals-card">
            <div className="sld-totals-label">Remaining</div>
            <div
              className="sld-totals-value"
              data-testid="sld-remaining"
              style={{ color: totals.remaining > 0 ? '#E24B5B' : '#1DBF73' }}
            >
              {Number(totals.remaining).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      )}

      <div className="sld-table-card">
        <div className="sld-table-header">
          <span>{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="sld-table">
          {items.length === 0 && (
            <div className="empty-state" data-testid="sld-empty">No items yet. Add your first item below.</div>
          )}

          {items.map((it) => {
            const completed = it.status === 'done' || it.completed === true;
            return (
              <div key={it.id} className={`sld-row ${completed ? 'is-done' : ''}`} data-testid="sld-row">
                {cols.check && (
                  <button
                    className={`sld-check ${completed ? 'checked' : ''}`}
                    onClick={() => updateItem(it.id, { completed: !completed })}
                    aria-label={completed ? 'Mark as open' : 'Mark as done'}
                    data-testid="sld-check"
                  >
                    {completed && <FiCheck size={14} />}
                  </button>
                )}
                <div className="sld-row-main">
                  <input
                    className="sld-text-input"
                    value={it.text || ''}
                    onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x)))}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next && next !== it.text) updateItem(it.id, { text: next });
                    }}
                    data-testid="sld-row-text"
                  />
                  {cols.description && (
                    <input
                      className="sld-desc-input"
                      placeholder="Description"
                      value={it.description || ''}
                      onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, description: e.target.value } : x)))}
                      onBlur={(e) => updateItem(it.id, { description: e.target.value })}
                    />
                  )}
                </div>

                {cols.qty && (
                  <div className="sld-cell sld-cell-num">
                    <input
                      type="number"
                      step="0.01"
                      className="sld-num-input"
                      placeholder="Qty"
                      value={it.quantity ?? ''}
                      onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, quantity: e.target.value } : x)))}
                      onBlur={(e) => {
                        const v = e.target.value === '' ? null : parseFloat(e.target.value);
                        updateItem(it.id, { quantity: v });
                      }}
                    />
                  </div>
                )}
                {cols.money && (
                  <>
                    <div className="sld-cell sld-cell-num">
                      <input
                        type="number"
                        step="0.01"
                        className="sld-num-input"
                        placeholder="Unit"
                        value={it.unitPrice ?? ''}
                        onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, unitPrice: e.target.value } : x)))}
                        onBlur={(e) => {
                          const v = e.target.value === '' ? null : parseFloat(e.target.value);
                          updateItem(it.id, { unitPrice: v });
                        }}
                      />
                    </div>
                    <div className="sld-cell sld-cell-num sld-amount">
                      <span data-testid="sld-row-amount">
                        {it.amount != null
                          ? Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '—'}
                      </span>
                    </div>
                  </>
                )}
                {cols.location && (
                  <div className="sld-cell">
                    <input
                      className="sld-text-input"
                      placeholder="Location"
                      value={it.location || ''}
                      onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, location: e.target.value } : x)))}
                      onBlur={(e) => updateItem(it.id, { location: e.target.value })}
                    />
                  </div>
                )}
                {cols.company && (
                  <div className="sld-cell">
                    <input
                      className="sld-text-input"
                      placeholder="Company"
                      value={it.responsibleCompany || ''}
                      onChange={(e) => setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, responsibleCompany: e.target.value } : x)))}
                      onBlur={(e) => updateItem(it.id, { responsibleCompany: e.target.value })}
                    />
                  </div>
                )}
                {cols.status && (
                  <div className="sld-cell">
                    <select
                      className="sld-status-select"
                      value={it.status || 'open'}
                      onChange={(e) => updateItem(it.id, { status: e.target.value, completed: e.target.value === 'done' })}
                      style={{ background: `${getStatusMeta(it.status || 'open').color}1A`, color: getStatusMeta(it.status || 'open').color }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  className="sld-row-delete"
                  onClick={() => deleteItem(it.id)}
                  aria-label="Delete item"
                  data-testid="sld-row-delete"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="sld-add-row" data-testid="sld-add-row">
          <input
            className="sld-text-input"
            placeholder="Add a new item..."
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
            data-testid="sld-new-item-input"
          />
          {cols.qty && (
            <input
              type="number"
              step="0.01"
              className="sld-num-input"
              placeholder="Qty"
              value={draft.quantity}
              onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
            />
          )}
          {cols.money && (
            <input
              type="number"
              step="0.01"
              className="sld-num-input"
              placeholder="Unit"
              value={draft.unitPrice}
              onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })}
            />
          )}
          {cols.location && (
            <input
              className="sld-text-input"
              placeholder="Location"
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
            />
          )}
          {cols.company && (
            <input
              className="sld-text-input"
              placeholder="Company"
              value={draft.responsibleCompany}
              onChange={(e) => setDraft({ ...draft, responsibleCompany: e.target.value })}
            />
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={addItem}
            disabled={adding || !draft.text.trim()}
            data-testid="sld-add-item-button"
          >
            <FiPlus size={14} /> Add
          </button>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 16 }}>{error}</div>}

      <ShareSettingsModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        list={list}
        token={token}
        onSaved={(updated) => { setList(updated); setShowShareModal(false); }}
      />
    </div>
  );
};

export default SharedListDetail;
